// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { FloatingTooltip, FloatingTooltipStyles } from "@werner/App/Components/FloatingTooltip/FloatingTooltip";
import HeaderClickable from "@werner/App/Components/Header/HeaderClickable";
import MiniQuickOrder from "@werner/App/Components/MiniQuickOrder/MiniQuickOrder";

import { CustomAppState } from "@werner/App/Store/Reducers";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import QuickOrder from "@werner/XDL/Icons/QuickOrder";
import { mergeWithCss } from "@werner/XDL/Utilities";
import React, { FC } from "react";
import { ThemeProps, withTheme } from "styled-components";

interface OwnProps extends WidgetProps {}

export interface QuickOrderStyles {
    floatingTooltipStyles?: FloatingTooltipStyles;
}

export const quickOrderStyles: QuickOrderStyles = {};

type HeaderQuickOrderTheme = BaseTheme & {
    headerQuickOrder?: QuickOrderStyles;
};

type Props = OwnProps & ThemeProps<HeaderQuickOrderTheme>;

export const HeaderQuickOrder: FC<Props> = ({ theme }) => {
    const styles = React.useMemo(
        () => mergeWithCss(quickOrderStyles, theme.headerQuickOrder),
        [theme.headerQuickOrder],
    );
    const [isPopupShown, setIsPopupShown] = React.useState(false);
    const { isMobileScreen, isTabletScreen, isAuthenticated, quickOrderPageUrl } = useAppSelector(state => ({
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        isAuthenticated: xIsAuthenticated(state),
        quickOrderPageUrl: getPageLinkByPageType(state, "QuickOrderPage")?.url,
    }));

    const quickOrderClickable = (
        <HeaderClickable
            iconProps={{ src: QuickOrder }}
            title={translate("Quick Order")}
            subTitle={translate("Bulk Ordering")}
            isOpen={isPopupShown}
            href={quickOrderPageUrl}
        />
    );

    // TODO
    if (!isAuthenticated) {
        return null;
    }

    if (isMobileScreen) {
        return null;
    }

    if (isTabletScreen) {
        return quickOrderClickable;
    }

    return (
        <FloatingTooltip
            extendedStyles={styles.floatingTooltipStyles}
            maxWidth={500}
            triggerComponent={quickOrderClickable}
            showArrow={true}
            offset={{ mainAxis: 16, crossAxis: 0 }}
            flip={{ fallbackPlacements: ["bottom"] }}
            placement="bottom"
            isOpen={isPopupShown}
            onOpenChange={isOpen => setIsPopupShown(isOpen)}
            theme={theme}
        >
            <MiniQuickOrder />
        </FloatingTooltip>
    );
};

const widgetModule: WidgetModule = {
    component: withTheme(HeaderQuickOrder),
    definition: {
        displayName: "Header Quick Order",
        icon: "arrow-right-to-bracket",
        group: "Common",
    },
};

export default widgetModule;
